import { Helmet } from 'react-helmet-async'

interface SEOProps {
  title?: string
  description?: string
  canonical?: string
  type?: 'website' | 'article' | 'product'
  image?: string
  imageAlt?: string
  noindex?: boolean
  article?: {
    publishedTime?: string
    modifiedTime?: string
    author?: string
  }
  structuredData?: object | object[]
}

const SITE_NAME = 'Ledger'
const DEFAULT_TITLE = 'Ledger | Simple Bookkeeping for Sri Lankan Businesses'
const DEFAULT_DESCRIPTION = 'Ledger replaces your paper ledger with a simple app to track income, expenses, and customer credit. No accounting knowledge needed. Works on any phone.'
const DEFAULT_IMAGE = 'https://ledger.lk/images/og-image.png'
const SITE_URL = 'https://ledger.lk'
const TWITTER_HANDLE = '@ledgerapp'

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  type = 'website',
  image = DEFAULT_IMAGE,
  imageAlt = 'Ledger - Simple Bookkeeping for Sri Lankan Businesses',
  noindex = false,
  article,
  structuredData,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : undefined

  // Default organization structured data
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Ledger',
    alternateName: 'Ledger',
    url: SITE_URL,
    logo: `${SITE_URL}/images/logo.png`,
    description: 'Simple bookkeeping app for micro and small businesses in Sri Lanka.',
    foundingDate: '2024',
    founders: [
      {
        '@type': 'Organization',
        name: 'Nanthi Ventures',
        url: 'https://www.nanthiventures.com',
      },
    ],
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'LK',
    },
    sameAs: [
      'https://www.linkedin.com/company/ledgerapp',
      'https://twitter.com/ledgerapp',
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'sales',
        email: 'sales@ledger.lk',
      },
      {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'support@ledger.lk',
      },
    ],
  }

  // Default website structured data
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
    publisher: {
      '@type': 'Organization',
      name: 'Ledger',
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  }

  // Collect all structured data
  const allStructuredData = [
    organizationSchema,
    websiteSchema,
    ...(Array.isArray(structuredData) ? structuredData : structuredData ? [structuredData] : []),
  ]

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:locale" content="en_US" />
      
      {/* Article specific OG tags */}
      {type === 'article' && article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {type === 'article' && article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {type === 'article' && article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />
      
      {/* Additional SEO tags */}
      <meta name="author" content="Ledger" />
      <meta name="publisher" content="Ledger" />
      <meta name="copyright" content="© 2024-2026 Ledger All rights reserved." />
      
      {/* Language and region */}
      <meta name="language" content="English" />
      <meta name="geo.region" content="LK" />
      <meta name="geo.placename" content="Colombo" />
      
      {/* Theme and app */}
      <meta name="theme-color" content="#08c6ea" />
      <meta name="msapplication-TileColor" content="#08c6ea" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={SITE_NAME} />
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(allStructuredData)}
      </script>
    </Helmet>
  )
}

// Pre-built structured data generators for different page types
export const StructuredData = {
  // Software Application schema for the product
  softwareApplication: () => ({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Ledger',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, iOS, Android',
    description: 'Simple bookkeeping app for micro and small businesses. Track income, expenses, and customer credit from any device.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'LKR',
      description: 'Free to start',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '142',
    },
    featureList: [
      'Income and expense tracking',
      'Customer credit management',
      'Receipt capture',
      'Financial reports',
      'Cloud backup',
      'Works on any device',
      'Accounting firm partnerships',
    ],
  }),

  // FAQ schema builder
  faq: (questions: { question: string; answer: string }[]) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer,
      },
    })),
  }),

  // Breadcrumb schema builder
  breadcrumb: (items: { name: string; url: string }[]) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `https://ledger.lk${item.url}`,
    })),
  }),

  // Service schema for pricing tiers
  service: (name: string, description: string, price?: string) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    provider: {
      '@type': 'Organization',
      name: 'Ledger',
    },
    ...(price && {
      offers: {
        '@type': 'Offer',
        price,
        priceCurrency: 'LKR',
      },
    }),
  }),

  // WebPage schema for generic pages
  webPage: (name: string, description: string, url: string) => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url: `https://ledger.lk${url}`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'Ledger',
      url: 'https://ledger.lk',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Ledger',
    },
  }),
}

export default SEO

